import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { QA_NAME_BACK_BTN } from '../../../constants/qa'
import { filterPermittedActionsWithDividers } from '../../../utils/massActions'
import { ICON_CHEVRON_DIRECTION, IconChevron } from '../../Icon'
import {
  getIsRequestArchiveBlockedByStatus,
  getRequestArchiveBlockedByStatusTooltip,
} from '../../Request'
import {
  isRequestProfileHeaderFooterActionPermitted,
  REQUEST_PROFILE_HEADER_FOOTER_ACTION_ITEMS,
} from '../RequestProfileHeaderFooter'
import { getToRecipients } from '../RequestProfileTabEmails'

import {
  REQUEST_PROFILE_ACTIONS_WIDGET_ACTION,
  REQUEST_PROFILE_ACTIONS_WIDGET_ADD_NEW_OPTION_LABEL_KEY,
  REQUEST_PROFILE_ACTIONS_WIDGET_ATTACHED_REQUESTS_OPTION,
} from './RequestProfileActionsWidget.constants'
import { RequestProfileActionsWidgetAddNewButton } from './RequestProfileActionsWidgetAddNewButton'
import { RequestProfileActionsWidgetAttachedRequestsButton } from './RequestProfileActionsWidgetAttachedRequestsButton'

import ArchiveIcon from '../../../icons/archive.svg'
import ClosedEye from '../../../icons/closed-eye.svg'
import OpenEye from '../../../icons/open-eye.svg'
import RestoreIcon from '../../../icons/restore.svg'
import ServiceRequestConnectIcon from '../../../icons/service-request-connect.svg'
import ServiceRequestUnreadIcon from '../../../icons/service-request-unread.svg'

export function getRequestProfileActionsWidgetAddNewOptions({
  isAppointmentAvailable,
  requestData,
  sections,
  t,
}) {
  const permissions = requestData?.permissions

  if (permissions) {
    return REQUEST_PROFILE_HEADER_FOOTER_ACTION_ITEMS.filter(({ action }) =>
      isRequestProfileHeaderFooterActionPermitted({
        action,
        isAppointmentAvailable,
        requestData,
        sections,
      })
    ).map(({ action }) => ({
      label: t(REQUEST_PROFILE_ACTIONS_WIDGET_ADD_NEW_OPTION_LABEL_KEY[action]),
      permission: true,
      value: action,
    }))
  }

  return EMPTY_LIST
}

export function getRequestProfileActionsWidgetAttachedRequestsOptions({
  relatedRequestsCount,
  requestData,
  t,
}) {
  const permissions = requestData?.permissions

  if (permissions) {
    return [
      {
        label: t('RequestProfile.ActionsWidgetAttachedRequestsCreateKey'),
        permission: permissions.can_create_related_request,
        value: REQUEST_PROFILE_ACTIONS_WIDGET_ATTACHED_REQUESTS_OPTION.create,
      },
      {
        label: t('RequestProfile.ActionsWidgetAttachedRequestsAttachKey'),
        permission: permissions.can_create_related_request,
        value: REQUEST_PROFILE_ACTIONS_WIDGET_ATTACHED_REQUESTS_OPTION.attach,
      },
      {
        label: t('RequestProfile.ActionsWidgetAttachedRequestsMassEditKey'),
        permission:
          permissions.can_edit_related_requests && relatedRequestsCount > 0,
        value: REQUEST_PROFILE_ACTIONS_WIDGET_ATTACHED_REQUESTS_OPTION.massEdit,
      },
    ].filter(option => option.permission)
  }

  return EMPTY_LIST
}

export function getRequestProfileActionsWidgetItems({
  addNewOptions,
  attachedRequestsOptions,
  requestData,
  t,
  ukConfig,
  onClickBack,
  onSelectAddNewOption,
  onSelectAttachedRequestsOption,
}) {
  const permissions = requestData?.permissions || EMPTY_OBJECT

  const isArchived = requestData?.isArchived

  const isArchiveBlockedByStatus =
    !isArchived &&
    getIsRequestArchiveBlockedByStatus({
      statusId: requestData?.status?.id,
      ukConfig,
    })

  const items = [
    {
      beforeIconProps: {
        Icon: IconChevron,
        iconProps: { direction: ICON_CHEVRON_DIRECTION.LEFT },
      },
      permission: Boolean(onClickBack),
      qaName: QA_NAME_BACK_BTN,
      tooltip: t('Profile.BackBtnKey'),
      value: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.back,
    },
    {
      Component: RequestProfileActionsWidgetAddNewButton,
      options: addNewOptions,
      permission: !isArchived && addNewOptions.length > 0,
      qaName: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.addNew,
      tooltip: t('RequestProfile.ActionsWidgetAddNewTooltipKey'),
      value: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.addNew,
      onSelectOption: onSelectAddNewOption,
    },
    {
      beforeIconProps: {
        Icon: requestData?.visibleToDwellers ? OpenEye : ClosedEye,
      },
      permission: !isArchived && permissions.can_edit_visible_to_dwellers,
      qaName: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.toggleVisibleToDwellers,
      tooltip: t(
        requestData?.visibleToDwellers
          ? 'RequestProfile.ActionsWidgetHideForDwellersTooltipKey'
          : 'RequestProfile.ActionsWidgetShowForDwellersTooltipKey'
      ),
      value: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.toggleVisibleToDwellers,
    },
    {
      beforeIconProps: { Icon: ServiceRequestUnreadIcon },
      permission: !isArchived && permissions.can_edit_unread_status,
      qaName: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.markAsUnread,
      tooltip: t('RequestProfile.ActionsWidgetMarkAsUnreadTooltipKey'),
      value: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.markAsUnread,
    },
    {
      Component: RequestProfileActionsWidgetAttachedRequestsButton,
      Icon: ServiceRequestConnectIcon,
      options: attachedRequestsOptions,
      permission: !isArchived && attachedRequestsOptions.length > 0,
      qaName: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.attachedRequests,
      tooltip: t('RequestProfile.ActionsWidgetAttachedRequestsTooltipKey'),
      value: REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.attachedRequests,
      onSelectOption: onSelectAttachedRequestsOption,
    },
    {
      beforeIconProps: {
        Icon: isArchived ? RestoreIcon : ArchiveIcon,
      },
      disabled: isArchiveBlockedByStatus,
      permission: isArchived ? permissions.can_restore : permissions.can_delete,
      qaName: isArchived
        ? REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.restore
        : REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.archive,
      tooltip: isArchiveBlockedByStatus
        ? getRequestArchiveBlockedByStatusTooltip(t, requestData?.status?.id)
        : t(
            isArchived
              ? 'RequestProfile.ActionsWidgetRestoreTooltipKey'
              : 'RequestProfile.ActionsWidgetArchiveTooltipKey'
          ),
      value: isArchived
        ? REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.restore
        : REQUEST_PROFILE_ACTIONS_WIDGET_ACTION.archive,
    },
  ]

  return filterPermittedActionsWithDividers(items)
}

export function getRequestProfileActionsWidgetCreateEmailInitialValues({
  emailInitial,
  recipientType,
  requestContractors,
  requestData,
}) {
  return {
    ...emailInitial,
    to: getToRecipients({ recipientType, requestContractors, requestData }),
  }
}
